import { useState } from 'react';
import { SlidersHorizontal, ChevronDown, RotateCcw } from 'lucide-react';

// "Today's Adjustments" — Phase 1 (read-only).
// One collapsible disclosure that aggregates EVERY engine which quietly held,
// removed, swapped, or eased part of today's scheduled strength session, and says
// why. Lives in the banner area so "keep today vs. restore full" is a decidable
// choice instead of a silent omission. It does NOT replace the individual engine
// banners (auto-reg, prosthesis, overtraining, pain) — those keep their own
// headlines / colors / controls. Phase 2 will make each row individually
// reclaimable ("restore this one move") here and on the prosthesis banner + AI modal.
//
// Props:
//   adjustments: { count: number, groups: [{ key, title, reason, items: [{name, detail}] }] }
//   onRestore?:  optional handler — when provided, the footer shows a real
//                "restore" button (used by the free rest-day stretch routine,
//                which reduces itself for flagged areas and offers a one-tap
//                restore-to-full). When absent, the generic Phase-1 footer shows.
//   restoreLabel?: button copy (default "Restore full").
export default function TodaysAdjustments({ adjustments, onRestore, restoreLabel }) {
  const [open, setOpen] = useState(false);
  if (!adjustments || adjustments.count === 0) return null;
  const { groups, count } = adjustments;

  return (
    <div
      className="mb-5 rounded-lg bg-enf-surface-special/30 border border-enf-border-special/30"
      data-testid="todays-adjustments"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="todays-adjustments-toggle"
        className="w-full flex items-center gap-3 p-4 text-left"
      >
        <SlidersHorizontal className="w-4 h-4 text-enf-role-info flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold mb-0.5">
            Today's adjustments
          </div>
          <div className="text-sm text-enf-text-secondary">
            {count} {count === 1 ? 'change' : 'changes'} tailored to how you're training today — tap to see what &amp; why.
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-enf-text-secondary flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-3 space-y-3 border-t border-enf-border-special/20">
          {groups.map((g) => (
            <div key={g.key} data-testid={`adjustment-${g.key}`}>
              <div className="text-sm font-semibold text-enf-text-primary">{g.title}</div>
              <div className="text-xs text-enf-text-secondary opacity-90 mb-1.5">{g.reason}</div>
              <ul className="space-y-1">
                {g.items.map((it, i) => (
                  <li key={i} className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-enf-text-secondary">{it.name}</span>
                    {it.detail && (
                      <span className="text-[11px] uppercase tracking-wide text-enf-text-secondary opacity-60 flex-shrink-0">
                        {it.detail}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {onRestore ? (
            <button
              type="button"
              onClick={onRestore}
              data-testid="todays-adjustments-restore"
              className="inline-flex items-center gap-1.5 pt-1 text-xs font-semibold text-enf-role-info hover:text-enf-text-primary transition-colors"
            >
              <RotateCcw className="w-3 h-3 flex-shrink-0" />
              {restoreLabel || 'Restore full'}
            </button>
          ) : (
            <div className="flex items-start gap-1.5 pt-1 text-xs text-enf-text-secondary opacity-70">
              <RotateCcw className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>These are eased to protect how you're training today. Where a notice above has a restore control, you can reverse it there — per-item restore is coming soon.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
